































































import Vue from 'vue';
import { Data as ArticleData } from '@root/modules/article/types/article';
import { Data as HeadlinesData, DataWithHref } from '@root/modules/headlines/types/headlines';
import ArticleHeadline from './ArticleHeadline.vue';
import UniversalHeadlinesService from '@root/modules/headlines/services/UniversalHeadlines.service';
import ArticleCxenseService from '@root/modules/article/services/ArticleCxense.service';
import getHeadlineWithHref from '@root/modules/headlines/utils/getHeadlineWithHref';
import AdFormBanner from '@ads/components/adForm/AdFormBanner.vue';
import getBannerKey from '@root/modules/ads/utils/getBannerKey';
import getArticleUrl from '@root/modules/article/utils/buildArticleUrl';

interface Props {
  article: ArticleData;
}

interface Methods {
  addClickData: (headline: HeadlinesData, params: Record<string, any>) => Record<string, any>;
  getHeadline: (headline: HeadlinesData) => DataWithHref;
  getCxenseHeadline: (headline: HeadlinesData) => DataWithHref;
  getCxenseHeadlines: () => void;
  getArticleHeadlines: (relatedHeadlineIds: number[]) => Promise<HeadlinesData[]>;
  getKey: (params: Record<string, any>) => string;
}

interface Computed {
  mostBoughtArticles: number[];
  disableAds: boolean;
  isCrArticle: boolean;
}

interface Data {
  domains: string | string[];
  locale: string;
  language: string;
  mostBoughtHeadlines: HeadlinesData[];
  cXenseHeadlinesLimit: number;
  cXenseHeadlines: HeadlinesData[];
  cXenseArticles: {
    id: string;
    clickUrl: string;
  }[];
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    ArticleHeadline,
    AdFormBanner,
  },
  props: {
    article: {
      type: Object,
      required: true,
    },
  },
  data() {
    const { domain, locale, temporaryAllowedDomains, lang } = this.$channelConfig('settings');
    const domains = temporaryAllowedDomains ? [...new Set([...temporaryAllowedDomains, domain])] : domain;
    return {
      domains,
      locale,
      language: lang.toUpperCase(),
      mostBoughtHeadlines: [],
      cXenseHeadlines: [],
      cXenseHeadlinesLimit: 20,
      cXenseArticles: [],
    };
  },
  computed: {
    mostBoughtArticles() {
      const articleExtra = this.$store.getters['article/getArticleExtraContent'];
      return articleExtra && articleExtra.most_bought?.length > 0 ? articleExtra.most_bought : null;
    },
    disableAds() {
      return this.article.banners.disableAds;
    },
    isCrArticle() {
      const articles = this.$store.getters['article/getCrArticles'];

      return articles.find((article: number) => article === this.article.id);
    },
  },
  watch: {
    mostBoughtArticles: {
      async handler(articleExtra) {
        if (articleExtra) {
          this.mostBoughtHeadlines = await this.getArticleHeadlines(articleExtra);
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.getCxenseHeadlines();
    });
  },
  methods: {
    addClickData(headline: HeadlinesData, params: Record<string, any>) {
      const clickData: Record<string, any> = {};

      clickData.articleLayout = 'a';
      clickData.listType = params.listType;
      clickData.listPosition = params.listPosition;
      clickData.listArticleId = headline.id;
      clickData.listArticleUrl = getArticleUrl({ article: headline, domain: headline.primaryCategory.channel.url });
      clickData.listArticlePosition = params.listIndex;

      return { ...headline, clickData };
    },
    getHeadline(headline) {
      const fakeDomainEnabled = this.$config.FAKE_DOMAIN_ENABLED;
      return getHeadlineWithHref({ headline, domain: this.domains, fakeDomainEnabled });
    },
    getCxenseHeadline(headline) {
      const cXenseArticle = this.cXenseArticles.find(article => article.id === String(headline.id));
      return cXenseArticle ? { ...headline, href: cXenseArticle?.clickUrl } : this.getHeadline(headline);
    },
    async getCxenseHeadlines() {
      const widgetId = this.$channelConfig('page').article.component.cXense?.widgetId;

      if (!widgetId) {
        return false;
      }

      // Get cXense page context for more relevant data
      const articleCxenseService = new ArticleCxenseService();
      this.cXenseArticles = await articleCxenseService.fetch({ widgetId });
      this.cXenseHeadlines = this.cXenseArticles ? await this.getArticleHeadlines(this.cXenseArticles.map(article => Number(article.id))) : [];
    },
    async getArticleHeadlines(relatedHeadlineIds) {
      const universalHeadlinesService = new UniversalHeadlinesService();
      // FIX ME: Removed language: this.language variable as content-api-v3 has now channel.language: "ET" imported;
      const headlines = await universalHeadlinesService.fetch({ id: relatedHeadlineIds, limit: this.cXenseHeadlinesLimit });
      return headlines.items;
    },
    getKey({ prefix, index }) {
      return getBannerKey({ prefix, route: this.$route, store: this.$store, index });
    },
  },
});
